/**
 * Require explicit variant selection.
 *
 * When a <variant-selects> is rendered with data-require-selection="true" the
 * theme intentionally leaves every option unselected (a "Select …" placeholder
 * is shown instead). This controller keeps things in that state until the
 * customer has actively chosen a value for every option:
 *
 *  - Records the customer's real choices per option (survives the DOM being
 *    re-rendered by product-info on every change).
 *  - After each re-render, Shopify auto-fills any option the customer has not
 *    touched with its first available value — we reset those back to the
 *    placeholder so nothing is silently pre-selected.
 *  - While any option is still on its placeholder, the add-to-cart button, the
 *    hidden variant id input, the dynamic checkout (Shop Pay) button and the
 *    price are all disabled/hidden.
 *
 * Once every option has a real value, control is handed back to Dawn's normal
 * product-info flow.
 */
(function () {
  const SELECT_PROMPT = 'Select options';
  const INCOMPLETE_CLASS = 'variant-selection-incomplete';
  const INFO_IDS = ['price', 'Sku', 'Inventory', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules'];

  // sectionId -> Map(optionName -> chosen value). Lives outside the DOM so it
  // persists across variant-selects re-renders.
  const sectionState = new Map();

  function getState(sectionId) {
    if (!sectionState.has(sectionId)) sectionState.set(sectionId, new Map());
    return sectionState.get(sectionId);
  }

  function optionNameFromSelect(select) {
    const match = /options\[(.+)\]/.exec(select.getAttribute('name') || '');
    return match ? match[1] : select.getAttribute('name') || '';
  }

  function isPlaceholder(option) {
    return option.hasAttribute('data-placeholder');
  }

  function getGroups(variantSelects) {
    const groups = [];
    variantSelects.querySelectorAll('select').forEach((select) => {
      groups.push({ type: 'select', name: optionNameFromSelect(select), select });
    });
    variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      if (!inputs.length) return;
      groups.push({ type: 'radio', name: inputs[0].dataset.optionName || '', inputs });
    });
    return groups;
  }

  function selectPlaceholder(select) {
    Array.from(select.options).forEach((option) => {
      const keep = isPlaceholder(option);
      option.selected = keep;
      if (keep) {
        option.setAttribute('selected', 'selected');
      } else {
        option.removeAttribute('selected');
      }
    });
  }

  function selectValue(select, value) {
    let matched = false;
    Array.from(select.options).forEach((option) => {
      const isMatch = !isPlaceholder(option) && option.value === value;
      option.selected = isMatch;
      if (isMatch) {
        option.setAttribute('selected', 'selected');
        matched = true;
      } else {
        option.removeAttribute('selected');
      }
    });
    return matched;
  }

  function getSelectedVariant(variantSelects) {
    try {
      return JSON.parse(variantSelects.querySelector('script[data-selected-variant]')?.textContent);
    } catch {
      return null;
    }
  }

  function applyButtonState(submitButton, idInput, variant) {
    if (!submitButton) return;
    const label = submitButton.querySelector('span');
    const strings = window.variantStrings || {};
    if (variant && variant.available) {
      submitButton.removeAttribute('disabled');
      idInput?.removeAttribute('disabled');
      if (label) label.textContent = strings.addToCart || 'Add to cart';
    } else {
      submitButton.setAttribute('disabled', 'disabled');
      idInput?.setAttribute('disabled', 'disabled');
      if (label) label.textContent = variant ? strings.soldOut || 'Sold out' : strings.unavailable || 'Unavailable';
    }
  }

  function toggleInfo(sectionId, hide) {
    if (!hide) return; // product-info restores visibility once a variant resolves
    INFO_IDS.forEach((id) => {
      const el = document.getElementById(`${id}-${sectionId}`);
      if (el) el.classList.add('hidden');
    });
  }

  function applyState(variantSelects) {
    if (!variantSelects || variantSelects.dataset.requireSelection !== 'true') return;

    const sectionId = variantSelects.dataset.section;
    const state = getState(sectionId);
    const groups = getGroups(variantSelects);
    let allChosen = groups.length > 0;

    groups.forEach((group) => {
      const chosen = state.get(group.name);

      if (group.type === 'select') {
        if (chosen != null && selectValue(group.select, chosen)) return;
        // Not chosen yet, or the previously chosen value is no longer offered.
        state.delete(group.name);
        selectPlaceholder(group.select);
        allChosen = false;
      } else {
        let matched = false;
        group.inputs.forEach((input) => {
          const isMatch = chosen != null && input.value === chosen;
          input.checked = isMatch;
          if (isMatch) matched = true;
        });
        if (!matched) {
          state.delete(group.name);
          allChosen = false;
        }
      }
    });

    const productForm = document.getElementById(`product-form-${sectionId}`)?.closest('product-form');
    const idInput = productForm?.querySelector('input[name="id"]');
    const submitButton = document.getElementById(`ProductSubmitButton-${sectionId}`);

    if (allChosen) {
      productForm?.classList.remove(INCOMPLETE_CLASS);
      // The server always renders the submit button disabled while
      // require-selection is on, which makes Dawn's product-info flow think the
      // variant is sold out. Now that every option is chosen we own the button
      // and base its state on the actually-resolved variant instead.
      const variant = getSelectedVariant(variantSelects);
      applyButtonState(submitButton, idInput, variant);
    } else {
      productForm?.classList.add(INCOMPLETE_CLASS);
      idInput?.setAttribute('disabled', 'disabled');
      if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
        const label = submitButton.querySelector('span');
        if (label) label.textContent = SELECT_PROMPT;
      }
      toggleInfo(sectionId, true);
    }
  }

  // Record the customer's real choices. Capture phase runs before Dawn's own
  // change handler (which triggers the fetch/re-render), so state is up to date
  // by the time we re-apply it after the render.
  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      const variantSelects =
        target.closest && target.closest('variant-selects[data-require-selection="true"]');
      if (!variantSelects) return;

      const state = getState(variantSelects.dataset.section);

      if (target.tagName === 'SELECT') {
        const name = optionNameFromSelect(target);
        const option = target.selectedOptions[0];
        if (option && !isPlaceholder(option) && option.value) {
          state.set(name, option.value);
        } else {
          state.delete(name);
        }
      } else if (target.tagName === 'INPUT' && target.type === 'radio' && target.value) {
        state.set(target.dataset.optionName || '', target.value);
      }
    },
    true
  );

  function observeProductInfo(productInfo) {
    if (productInfo.dataset.requireSelectionObserved === 'true') return;
    productInfo.dataset.requireSelectionObserved = 'true';

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          const variantSelects =
            node.matches?.('variant-selects') ? node : node.querySelector?.('variant-selects');
          if (variantSelects) applyState(variantSelects);
        }
      }
    });
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  function init(root = document) {
    root.querySelectorAll('variant-selects[data-require-selection="true"]').forEach((variantSelects) => {
      const productInfo = variantSelects.closest('product-info');
      if (productInfo) observeProductInfo(productInfo);
      applyState(variantSelects);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
